import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { Plus, X, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import Footer from "@/components/Footer";

export default function Universe() {
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSym, setNewSym] = useState("");
  const [defaults, setDefaults] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, d] = await Promise.all([
        api.get("/me/universe"),
        api.get("/universe"),
      ]);
      setSymbols(u.data.symbols);
      setDefaults(d.data.symbols);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e?.preventDefault?.();
    const s = newSym.trim().toUpperCase();
    if (!s) return;
    try {
      const r = await api.post("/me/universe", { symbol: s });
      setSymbols(r.data.symbols);
      setNewSym("");
      toast.success(`${s} added to universe`);
    } catch { toast.error("Add failed"); }
  };

  const remove = async (s) => {
    try {
      const r = await api.delete(`/me/universe/${s}`);
      setSymbols(r.data.symbols);
    } catch { toast.error("Remove failed"); }
  };

  const reset = async () => {
    if (!window.confirm("Reset to default universe? This replaces your current list.")) return;
    try {
      const r = await api.post("/me/universe/reset");
      setSymbols(r.data.symbols);
      toast.success("Reset to defaults");
    } catch { toast.error("Reset failed"); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopBar />
      <div className="px-6 py-5 max-w-4xl mx-auto">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="micro-label">/ tracked universe</div>
            <h1 className="font-heading text-3xl mt-1">My Universe</h1>
            <p className="text-xs text-[#8a8a8e] mt-1 font-mono-data">
              These tickers power the dashboard momentum ranking. Default = {defaults.length} symbols.
            </p>
          </div>
          <button onClick={reset} data-testid="universe-reset"
            className="flex items-center gap-1 px-3 py-1.5 border border-[#222] hover:border-[#ffcc00] hover:text-[#ffcc00] text-xs font-mono-data uppercase tracking-wider">
            <ArrowCounterClockwise size={14}/> RESET
          </button>
        </div>

        <form onSubmit={add} className="flex gap-2 mb-4">
          <input
            placeholder="ADD TICKER (e.g. NVDA, PLTR, SOFI)"
            value={newSym}
            onChange={e=>setNewSym(e.target.value.toUpperCase())}
            className="flex-1 px-3 py-2 bg-[#0f0f0f] border border-[#222] focus:border-[#444] outline-none font-mono-data text-sm uppercase placeholder:text-[#555]"
            data-testid="universe-add-input"
          />
          <button type="submit" data-testid="universe-add-btn"
            className="flex items-center gap-1 px-4 py-2 bg-white text-black hover:bg-[#00e676] text-xs font-mono-data uppercase tracking-wider">
            <Plus size={14} weight="bold"/> ADD
          </button>
        </form>

        <div className="panel p-4">
          <div className="micro-label mb-3">{symbols.length} symbols</div>
          {loading && <div className="font-mono-data text-xs text-[#555] py-4">LOADING…</div>}
          <div className="flex flex-wrap gap-2" data-testid="universe-list">
            {symbols.map(s => (
              <span key={s} className="group inline-flex items-center gap-2 px-3 py-1.5 bg-[#0a0a0a] border border-[#222] hover:border-[#444] font-mono-data text-sm">
                {s}
                <button onClick={() => remove(s)} data-testid={`universe-remove-${s}`}
                  className="text-[#555] hover:text-[#ff3b30]">
                  <X size={12}/>
                </button>
              </span>
            ))}
            {!loading && symbols.length === 0 && (
              <span className="font-mono-data text-xs text-[#555]">UNIVERSE EMPTY · ADD TICKERS OR RESET</span>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
